import React from 'react';
import type { LearningProgress } from './useLearningProgress';

interface LearningModuleData {
  title: string;
  subtitle: string;
  estimatedTime: string;
  cards: Array<{
    id: string;
    label: string;
    title: string;
    color: string;
    icon: string;
  }>;
}

interface Props {
  module: LearningModuleData | null;
  progress: LearningProgress;
  isComplete: boolean;
  hasStarted: boolean;
  onOpen: () => void;
}

const LearningModuleTriggerCard: React.FC<Props> = ({
  module,
  progress,
  isComplete,
  hasStarted,
  onOpen
}) => {
  if (!module) return null;

  if (isComplete) {
    return (
      <div className="max-w-[812px] mx-auto px-4 md:px-0 mb-4">
        <div
          className="rounded-[14px] border border-emerald-200 px-5 py-3 flex items-center justify-between"
          style={{ backgroundColor: '#ecfdf5' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#059669"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <span className="text-[13px] font-bold text-emerald-800">Background complete</span>
              <span className="ml-2 text-[11px] text-emerald-600">
                {module.cards.length}/{module.cards.length} concepts · quiz passed
              </span>
            </div>
          </div>
          <button
            onClick={onOpen}
            className="text-[12px] font-semibold text-emerald-700 hover:text-emerald-900 transition-colors"
          >
            Review ↗
          </button>
        </div>
      </div>
    );
  }

  const completedCount = progress.completedCards.length;
  const totalCards = module.cards.length;

  return (
    <div className="max-w-[812px] mx-auto px-4 md:px-0 mb-4">
      <div
        className="bg-white rounded-[14px] border border-[#e2e8f0] overflow-hidden px-5 py-5"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      >
        <div className="flex items-start gap-3.5">
          {/* Book icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className="text-[15px] font-bold text-slate-900"
              style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
            >
              New to this topic?
            </h3>
            <p
              className="mt-0.5 text-[13.5px] text-[#64748b] leading-relaxed"
              style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
            >
              Learn {totalCards} key concepts in {module.estimatedTime} so you can evaluate each
              perspective on its merits — not just how it sounds.
            </p>

            {/* Concept chips */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {module.cards.map((card) => {
                const done = progress.completedCards.includes(card.id);
                return (
                  <span
                    key={card.id}
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors"
                    style={{
                      backgroundColor: done ? card.color + '18' : '#f1f5f9',
                      color: done ? card.color : '#64748b',
                      border: done ? `1px solid ${card.color}30` : '1px solid transparent'
                    }}
                  >
                    {done && (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {card.title}
                  </span>
                );
              })}
            </div>

            {/* CTA row */}
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={onOpen}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-[13px] font-bold text-white hover:bg-slate-800 transition-colors"
              >
                {hasStarted ? `Continue (${completedCount}/${totalCards})` : 'Get the background'}
              </button>
              <span className="text-[11px] text-[#94a3b8]">
                {module.estimatedTime} · includes a quick check
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningModuleTriggerCard;
